import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';

import RichText, { RichTextType } from '@/components/Richtext';
import Link, { LinkStyle, LinkType } from '@/components/Link';
import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import About, { AboutType } from '@/components/About';
import { isDarkTheme } from '@/utils/StyleUtils';
import Image, { ImageType } from '@/components/Image';

export type SingleCenteredBlockType = RichTextType & {
  id: string;
  __typename: string;
  backgroundColor: string;
  about?: AboutType;
  title: string;
  image?: ImageType;
  primaryLink?: LinkType;
  secondaryLink?: LinkType;
};
type Props = SingleCenteredBlockType;

const MainWrapper = styled.div`
  text-align: center;
`;

const BlockImage = styled(Image)`
  width: 100%;
  max-width: 360px;
  margin-bottom: 4rem;
  border-radius: 24px;
`;

const CTAWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: center;
  width: 100%;
`;

const SingleCenteredBlock: React.FC<Props> = (props) => {
  const color: string = props.backgroundColor;
  const dark = isDarkTheme(color);

  const richTextCustomRenderer = {
    Paragraph: function CustomParagraph(node, children) {
      return (
        <Paragraph
          color={dark ? 'White' : 'GreyBlue'}
          size='lg'
          weight='semiBold'
        >
          {children}
        </Paragraph>
      );
    },
  };

  return (
    <BlockWrapper id={props.id} padding={'small'} color={color}>
      <Container>
        <MainWrapper>
          {props.about && <About data={props.about} dark={dark} />}
          <Title level='h2' size='xl' margin='0 0 16px 0' lightColor={dark}>
            {props.title}
          </Title>
          {props.richText && (
            <RichText
              data={props.richText}
              customComponents={richTextCustomRenderer}
              defaultPaths={props.defaultPaths}
              options={{
                theme: isDarkTheme(color) ? 'dark' : 'light',
                marginBottom: '4rem',
                parentContentType: 'SingleCenteredBlock',
              }}
              isInternalLinkButton
            />
          )}
          {props.image && <BlockImage {...props.image} />}
          <CTAWrapper>
            <Link {...props.primaryLink} linkStyle={LinkStyle.PRIMARY} />
            <Link {...props.secondaryLink} linkStyle={LinkStyle.SECONDARY} />
          </CTAWrapper>
        </MainWrapper>
      </Container>
    </BlockWrapper>
  );
};

export default SingleCenteredBlock;

export const singleCenteredBlockFragmentQuery = graphql`
  fragment SingleCenteredBlockFragment on ContentfulSingleCenteredBlock {
    id
    __typename
    backgroundColor
    about {
      ...AboutFragment
    }
    title
    richText {
      ...RichTextFragment
    }
    image {
      description
      gatsbyImageData(width: 720)
      ...ImageFragment
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
  }
`;
